import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ModuleTemplate from '../../ModuleTemplate';
import { useSpeakAnswer } from '../../lib/useSpeakAnswer';
import { normalizeText, wordSetOf, isSubsetOf, setsEqual } from '../../lib/speechAnswerEvaluator';
import { useInstructionAudio } from '../../lib/useInstructionAudio';
import './MissingItem.css';

const S3_BASE_URL = 'https://7cdc3d1b-1cdd-4f85-aaa4-fa5c412f8f13.selstorage.ru/vocabulary';

const IMAGE_MAP = {
  'виноград': 'grapes', 'киви': 'kiwi', 'черешня': 'cherries',
  'мороженое': 'ice_cream', 'пончик': 'donut', 'леденец': 'lollipop',
  'гамбургер': 'burger', 'сок': 'juice', 'шоколад': 'chocolate',
  'малина': 'raspberry', 'дыня': 'melon', 'варенье': 'jam',
  'морковь': 'carrot', 'капуста': 'cabbage', 'банан': 'banana'
};

const EXERCISE_DATA = [
  { id: 1, items: ['виноград', 'киви', 'черешня'], missing: 'киви', validAnswers: ['киви'] },
  { id: 2, items: ['мороженое', 'пончик', 'леденец'], missing: 'мороженое', validAnswers: ['мороженое', 'мороженого'] },
  { id: 3, items: ['гамбургер', 'сок', 'шоколад'], missing: 'шоколад', validAnswers: ['шоколад', 'шоколадка'] },
  { id: 4, items: ['малина', 'дыня', 'варенье'], missing: 'малина', validAnswers: ['малина', 'малину'] },
  { id: 5, items: ['морковь', 'капуста', 'банан'], missing: 'капуста', validAnswers: ['капуста', 'капусту'] },
];

const MissingItem = ({ ageGroup, onBack, onComplete }) => {
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [showResult, setShowResult] = useState(false);
  
  const [selectedMode, setSelectedMode] = useState(null);
  const [blinkTarget, setBlinkTarget] = useState('speak');
  
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [activeFeedback, setActiveFeedback] = useState(null);
  const [clickedItem, setClickedItem] = useState(null);
  const [gridItems, setGridItems] = useState([]);
  const [recognizerReady, setRecognizerReady] = useState(false);

  const { start: startSpeakRecognition, stop: stopSpeakRecognition } = useSpeakAnswer('MissingItem');
  const { play: playInstructionAudio, stop: stopInstructionAudio } = useInstructionAudio();

  useEffect(() => {
    const shuffled = [...EXERCISE_DATA].sort(() => Math.random() - 0.5);
    setQuestions(shuffled);
  }, []);

     // Умная генерация перемешанной сетки 3х3 (ИСПРАВЛЕНА)
  useEffect(() => {
    if (questions[currentIndex]) {
      const currentQ = questions[currentIndex];
      
      // 1. Создаем массив: по 3 штуки КАЖДОГО предмета (всего 9)
      let baseArray = [
        currentQ.items[0], currentQ.items[0], currentQ.items[0],
        currentQ.items[1], currentQ.items[1], currentQ.items[1],
        currentQ.items[2], currentQ.items[2], currentQ.items[2]
      ];
      
      // 2. Находим ОДИН из недостающих предметов и удаляем его
      const removeIdx = baseArray.indexOf(currentQ.missing);
      if (removeIdx !== -1) {
        baseArray.splice(removeIdx, 1);
      }
      
      // 3. Добавляем знак вопроса на место удаленного (снова 9 элементов)
      baseArray.push('?');
      
      let attempts = 0;
      let validGrid = [];
      
      // Функция проверки, что нигде нет 3-х в ряд или 2-х рядом
      const isValid = (arr) => {
        const lines = [
          [0, 1, 2], [3, 4, 5], [6, 7, 8], // Горизонтальные
          [0, 3, 6], [1, 4, 7], [2, 5, 8], // Вертикальные
          [0, 4, 8], [2, 4, 6]             // Диагонали
        ];
        for (const line of lines) {
          const [a, b, c] = line;
          if (arr[a] !== '?' && arr[a] === arr[b] && arr[b] === arr[c]) return false;
        }
        
        const pairs = [
          [0, 1], [1, 2], [3, 4], [4, 5], [6, 7], [7, 8], // Соседи по горизонтали
          [0, 3], [3, 6], [1, 4], [4, 7], [2, 5], [5, 8]  // Соседи по вертикали
        ];
        for (const pair of pairs) {
          const [a, b] = pair;
          if (arr[a] !== '?' && arr[a] === arr[b]) return false;
        }
        
        return true;
      };

      // Ищем валидный вариант
      while (attempts < 1000) {
        const shuffled = [...baseArray].sort(() => Math.random() - 0.5);
        if (isValid(shuffled)) {
          validGrid = shuffled;
          break;
        }
        attempts++;
      }
      
      // Если чудо не случилось, берем случайный (fallback)
      if (validGrid.length === 0) {
        validGrid = [...baseArray].sort(() => Math.random() - 0.5);
      }
      
      setGridItems(validGrid);
    }
  }, [currentIndex, questions]);

  useEffect(() => {
    if (selectedMode) return;
    const interval = setInterval(() => {
      setBlinkTarget(prev => (prev === 'speak' ? 'show' : 'speak'));
    }, 700);
    return () => clearInterval(interval);
  }, [selectedMode]);

  const currentQuestion = questions[currentIndex];
  const totalQuestions = questions.length;

  const wrongItems = useMemo(
    () => (currentQuestion ? currentQuestion.items.filter((item) => item !== currentQuestion.missing) : []),
    [currentQuestion]
  );
  const speechCandidates = useMemo(() => {
    if (!currentQuestion) return [];
    const correctCandidates = currentQuestion.validAnswers.map((text) => ({ key: 'correct', text }));
    const wrongCandidates = wrongItems.map((item, idx) => ({ key: `wrong-${idx}`, text: item }));
    return [...correctCandidates, ...wrongCandidates];
  }, [currentQuestion, wrongItems]);

  const correctWordSets = useMemo(
    () => (currentQuestion ? currentQuestion.validAnswers.map(wordSetOf) : []),
    [currentQuestion]
  );
  const wrongWordSets = useMemo(
    () => wrongItems.map((item) => ({ item, words: wordSetOf(item) })),
    [wrongItems]
  );

  const stopAll = useCallback(() => {
    stopSpeakRecognition();
    stopInstructionAudio();
  }, [stopSpeakRecognition, stopInstructionAudio]);

  const goToNextQuestion = () => {
    setCurrentIndex(prev => {
      if (prev < totalQuestions - 1) return prev + 1;
      setShowResult(true);
      return prev;
    });
  };

  const answeredRef = useRef(false);
  const heardWordsRef = useRef(new Set());

  const finishAnswer = (isCorrect, clickedName) => {
    if (answeredRef.current) return;
    answeredRef.current = true;
    stopAll();

    setIsSpeaking(false);
    setIsListening(false);
    setActiveFeedback({ type: isCorrect ? 'correct' : 'wrong', clicked: clickedName });
    setClickedItem(clickedName);
    setAnswers(prev => [...prev, isCorrect]);

    if (isCorrect && currentQuestion) {
      setGridItems(prev => prev.map(item => item === '?' ? currentQuestion.missing : item));
    }

    setTimeout(() => {
      setActiveFeedback(null);
      setClickedItem(null);
      setIsProcessing(false);
      goToNextQuestion();
    }, 2000);
  };

  const handleRawSpeechChunk = (text) => {
    if (answeredRef.current) return;
    normalizeText(text).split(' ').filter(Boolean).forEach((w) => heardWordsRef.current.add(w));
    const heard = heardWordsRef.current;
    if (heard.size === 0) return;

    if (correctWordSets.some((set) => setsEqual(set, heard))) {
      finishAnswer(true, currentQuestion.missing);
      return;
    }

    const stillPossible = correctWordSets.some((set) => isSubsetOf(heard, set));
    if (!stillPossible) {
      const matchedWrong = wrongWordSets.find((w) => setsEqual(w.words, heard));
      finishAnswer(false, matchedWrong ? matchedWrong.item : null);
    }
  };

  const processQuestion = useCallback(() => {
    if (!currentQuestion || showResult || !selectedMode) return;
    stopAll();

    setIsListening(true);
    setIsSpeaking(false);
    setIsProcessing(false);
    setActiveFeedback(null);
    setClickedItem(null);
    setRecognizerReady(false);
    answeredRef.current = false;
    heardWordsRef.current = new Set();

    const beginListeningOrInput = () => {
      setIsListening(false);
      setIsSpeaking(true);

      if (selectedMode === 'speak') {
        setIsProcessing(true);
        startSpeakRecognition(speechCandidates, {
          threshold: 2, // internal per-chunk matching disabled; handleRawSpeechChunk decides
          onListening: () => setRecognizerReady(true),
          onRawText: handleRawSpeechChunk,
          onResult: ({ timedOut }) => {
            if (timedOut) finishAnswer(false, null);
          },
        });
      }
    };

    playInstructionAudio('Посмотри на картинки. Чего не хватает?', beginListeningOrInput);
  }, [currentQuestion, showResult, selectedMode, playInstructionAudio, stopAll, startSpeakRecognition, speechCandidates, correctWordSets, wrongWordSets]);

  useEffect(() => {
    if (selectedMode) {
      processQuestion();
    }
  }, [currentIndex, selectedMode, processQuestion]);

  useEffect(() => {
    return () => stopAll();
  }, [stopAll]);

  useEffect(() => {
    if (!showResult) return;
    const timer = setTimeout(() => {
      const correctCount = answers.filter(a => a === true).length;
      const score = totalQuestions > 0 ? Math.round((correctCount / totalQuestions) * 100) : 0;
      if (onComplete) onComplete({ testId: 'MissingItem', score });
      else onBack();
    }, 3000);
    return () => clearTimeout(timer);
  }, [showResult, answers, totalQuestions, onComplete, onBack]);

  const handleOptionClick = (item) => {
    if (selectedMode !== 'show' || isProcessing || activeFeedback) return;
    setIsProcessing(true);
    finishAnswer(item === currentQuestion.missing, item);
  };

  if (showResult) {
    const correctCount = answers.filter(a => a === true).length;
    return (
      <ModuleTemplate progress={answers} totalQuestions={totalQuestions} forceStop={true} onComplete={onComplete || onBack}>
        <div className="mi-result-content">
          <h2 className="mi-result-title">🎉 Упражнение завершено!</h2>
          <div className="mi-result-score">
            <span className="mi-score-number">{correctCount}</span>
            <span className="mi-score-total"> / {totalQuestions}</span>
          </div>
        </div>
      </ModuleTemplate>
    );
  }

  if (!currentQuestion) {
    return (
      <ModuleTemplate progress={[]} totalQuestions={1} forceStop={true} onComplete={onComplete || onBack}>
        <div className="mi-loading-text">Загрузка...</div>
      </ModuleTemplate>
    );
  }

  return (
    <ModuleTemplate 
      progress={answers} 
      totalQuestions={totalQuestions} 
      forceStop={true} 
      onComplete={onComplete || onBack}
      disabled={!selectedMode || isProcessing}
      extraContent="Чего не хватает?"
    >
      <div className="mi-game-area">
        
        {!selectedMode && (
          <div className="mi-mode-overlay">
            <h2 className="mi-mode-title">Выбери режим</h2>
            <div className="mi-mode-buttons">
              <button 
                className="mi-mode-btn" 
                onClick={() => setSelectedMode('speak')}
                style={{
                  borderColor: blinkTarget === 'speak' ? '#7B4EF8' : '#E5E0F5',
                  background: blinkTarget === 'speak' ? '#F0EDFF' : '#FFFFFF',
                  boxShadow: blinkTarget === 'speak' ? '0 4px 12px rgba(123, 78, 248, 0.15)' : 'none'
                }}
              >
                🎙️ Говорить
              </button>
              <button 
                className="mi-mode-btn" 
                onClick={() => setSelectedMode('show')}
                style={{
                  borderColor: blinkTarget === 'show' ? '#7B4EF8' : '#E5E0F5',
                  background: blinkTarget === 'show' ? '#F0EDFF' : '#FFFFFF',
                  boxShadow: blinkTarget === 'show' ? '0 4px 12px rgba(123, 78, 248, 0.15)' : 'none'
                }}
              >
                👆 Показать
              </button>
            </div>
          </div>
        )}

        <div className="mi-main-container">
          
          <div className="mi-gameplay-row">
            
            {selectedMode === 'show' && (
              <div className="mi-options-col">
                {currentQuestion.items.map((item, idx) => {
                  const isCorrectChoice = activeFeedback?.type === 'correct' && item === clickedItem;
                  const isWrongClick = activeFeedback?.type === 'wrong' && item === clickedItem;
                  
                  return (
                    <div 
                      key={idx} 
                      className={`mi-option-card ${isCorrectChoice ? 'correct' : ''} ${isWrongClick ? 'wrong-click' : ''}`}
                      onClick={() => handleOptionClick(item)}
                      style={{ pointerEvents: !isProcessing ? 'auto' : 'none' }}
                    >
                      <img 
                        src={`${S3_BASE_URL}/${IMAGE_MAP[item] || 'placeholder'}.png`} 
                        alt={item} 
                        className="mi-option-img"
                        onError={(e) => { e.target.src = '/placeholder.png'; }} 
                      />
                    </div>
                  );
                })}
              </div>
            )}

            <div className="mi-grid-wrapper" style={{ opacity: !selectedMode ? 0.5 : 1, transition: 'opacity 0.3s' }}>
              <div className="mi-3x3-grid">
                {gridItems.map((item, index) => {
                  const isWrongClick = activeFeedback?.type === 'wrong' && item === clickedItem;
                  const showCorrectHint = activeFeedback && item === currentQuestion.missing && item !== '?';
                  
                  return (
                    <div 
                      key={index} 
                      className={`mi-grid-cell ${item === '?' ? 'mi-question-mark' : ''} ${isWrongClick ? 'wrong-click' : ''} ${showCorrectHint ? 'correct' : ''}`}
                    >
                      {item === '?' ? (
                        <span className="mi-question-icon">?</span>
                      ) : (
                        <img 
                          src={`${S3_BASE_URL}/${IMAGE_MAP[item] || 'placeholder'}.png`} 
                          alt={item} 
                          className="mi-grid-img"
                          onError={(e) => { e.target.src = '/placeholder.png'; }} 
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {selectedMode && (
              <div className="mi-indicators">
                <div className={`mi-indicator listen ${isListening ? 'active' : ''}`}>
                  <span>🎧</span>
                </div>
                <div className={`mi-indicator speak ${isSpeaking && (selectedMode !== 'speak' || recognizerReady) ? 'active' : ''}`}>
                  <span>🗣️</span>
                </div>
              </div>
            )}

          </div>

        </div>
      </div>
    </ModuleTemplate>
  );
};

export default MissingItem;